import { useEffect, useMemo, useState } from "react";
import { Modal } from "../../../components/base/Modal";
import { RadioGroup } from "../../../components/base/RadioGroup";
import { Textarea } from "../../../components/base/Textarea";
import { Alert } from "../../../components/base/Alert";
import { Button } from "../../../components/base/Button";
import { MEMBER_STATUSES, STATUS_LABEL } from "../../../config/members";
import type { Member, MemberStatus } from "../../../services/types";

interface ChangeStatusModalProps {
  open: boolean;
  onClose: () => void;
  /** One or many members to change status for (bulk). */
  members: Member[];
  onConfirm: (status: MemberStatus, reason: string) => void;
  disabled?: boolean;
}

export function ChangeStatusModal({ open, onClose, members, onConfirm, disabled }: ChangeStatusModalProps) {
  const [status, setStatus] = useState<MemberStatus>("active");
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStatus(members[0]?.status ?? "active");
      setReason("");
      setReasonError(null);
      setSubmitting(false);
    }
  }, [open, members]);

  const isBlacklist = status === "blacklisted";
  const displayName = useMemo(() => {
    if (members.length === 1) return members[0].fullName;
    return `${members.length} selected members`;
  }, [members]);

  const handleSubmit = () => {
    if (isBlacklist && !reason.trim()) {
      setReasonError("A reason is required to blacklist a member.");
      return;
    }
    setSubmitting(true);
    onConfirm(status, reason.trim());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change member status"
      description={displayName}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={disabled || submitting}>
            {isBlacklist ? "Blacklist member" : members.length > 1 ? `Update ${members.length} members` : "Update status"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <RadioGroup
          label="New status"
          name="member-status"
          direction="horizontal"
          value={status}
          onChange={(v) => {
            setStatus(v as MemberStatus);
            setReasonError(null);
          }}
          options={MEMBER_STATUSES.map((s) => ({ value: s.value, label: s.label }))}
        />

        {isBlacklist ? (
          <Alert variant="warning" title="Blacklisting is about future risk">
            A blacklisted member cannot be enrolled into any new group. Their existing group
            memberships and ongoing installments continue normally — this does not cancel any
            current obligations.
          </Alert>
        ) : null}

        <Textarea
          label="Reason"
          helper={isBlacklist ? "Required — this will be recorded on the member record." : "Optional note for this status change."}
          placeholder="Why is this status changing?"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setReasonError(null);
          }}
          error={reasonError}
          maxLength={500}
          rows={3}
        />

        {members.length > 1 ? (
          <p className="text-xs text-foreground-500">
            Applies {STATUS_LABEL[status].toLowerCase()} to all {members.length} selected members.
          </p>
        ) : null}
      </div>
    </Modal>
  );
}